import { Badge, Group, Switch } from '@mantine/core'
import { useIsMutating } from '@tanstack/react-query'
import cloneDeep from 'lodash.clonedeep'
import type { AdvancedFeatureResponse } from '#/api/models/advancedFeatureResponse'
import type { DataResponse } from '#/api/models/dataResponse'
import type { DataSupplementResponse } from '#/api/models/dataSupplementResponse'
import type { ResponseManualQualActionParams } from '#/api/models/responseManualQualActionParams'
import ButtonNew from '#/components/common/ButtonNew'
import Menu from '#/components/common/Menu'
import Icon from '#/components/common/icon'
import { userCan } from '#/components/permissions/utils'
import type { AssetResponse } from '#/dataInterface'
import { getAllTranslationsFromSupplementData, getLatestTranscriptVersionItem } from '../../common/utils'
import styles from './AnalysisHeader.module.scss'
import { ANALYSIS_QUESTION_TYPES } from './common/constants'
import { useShowHints } from './common/utils'

interface Props {
  asset: AssetResponse
  questionXpath: string
  submission: DataResponse
  supplement: DataSupplementResponse
  advancedFeatures: AdvancedFeatureResponse[]
  qaQuestion?: ResponseManualQualActionParams
  setQaQuestion: (qaQuestion: ResponseManualQualActionParams | undefined) => void
}

/**
 * This piece of UI is displaying the button/dropdown for adding new questions
 * (definitions). It also has a saving state indicator.
 */
export default function AnalysisHeader({ asset, questionXpath, supplement, qaQuestion, setQaQuestion }: Props) {
  const transcriptVersion = getLatestTranscriptVersionItem(supplement, questionXpath)
  const translationVersions = getAllTranslationsFromSupplementData(supplement, questionXpath)

  const [showHints, setShowHints] = useShowHints()

  // Note: Technically correct would be to filter for the 3 specific mutations we are interested in,
  //       but practically what else user would mutate in the meantime and no filter effectively is the same.
  const activeMutationCount = useIsMutating()

  const manualQuestionDefs = ANALYSIS_QUESTION_TYPES.filter((definition) => !definition.isAutomated)
  // TODO: we hide Keyword Search from the UI until https://github.com/kobotoolbox/kpi/issues/4594 is done
  // const automatedQuestionDefs = ANALYSIS_QUESTION_TYPES.filter((definition) => definition.isAutomated)
  const questionDefs = [
    ...manualQuestionDefs,
    // TODO: we hide Keyword Search from the UI until https://github.com/kobotoolbox/kpi/issues/4594 is done
    // ...(automatedQuestionDefs.length > 0 ? [t('Automated analysis')] : []),
    // ...automatedQuestionDefs,
  ]

  return (
    <header className={styles.root}>
      <Group>
        <Menu closeOnItemClick position='bottom-start' offset={2}>
          <Menu.Target>
            <ButtonNew
              size='md'
              leftIcon='plus'
              // We only allow editing one question at a time, so adding new is not
              // possible until user stops editing
              disabled={!userCan('manage_asset', asset) || !!qaQuestion}
            >
              {t('Add question')}
            </ButtonNew>
          </Menu.Target>

          <Menu.Dropdown className={styles.addQuestionMenu}>
            {questionDefs.map((questionDef) =>
              typeof questionDef === 'string' ? (
                <Menu.Label key={'title'}>{t('Automated analysis')}</Menu.Label>
              ) : (
                <Menu.Item
                  key={questionDef.type}
                  // We want to disable the Keyword Search question type when there is no transcript or translation.
                  disabled={
                    questionDef.type === 'qualAutoKeywordCount' &&
                    Boolean(transcriptVersion) &&
                    translationVersions.length === 0
                  }
                  onClick={() => setQaQuestion(cloneDeep(questionDef.placeholder))}
                  leftSection={<Icon name={questionDef.icon} size='m' />}
                >
                  {questionDef.label}
                </Menu.Item>
              ),
            )}
          </Menu.Dropdown>
        </Menu>

        <Switch
          label={t('Show hints')}
          checked={showHints}
          onChange={(event) => setShowHints(event.currentTarget.checked)}
        />
      </Group>

      <span>
        {activeMutationCount === 1 && (
          <Badge color='#fff7e6' styles={{ label: { color: '#B85A00' } }}>
            {t('Saving…')}
          </Badge>
        )}
        {activeMutationCount > 1 && (
          <Badge color='#fff7e6' styles={{ label: { color: '#B85A00' } }}>
            {t('Saving (##count##)…').replace('##count##', String(activeMutationCount))}
          </Badge>
        )}
        {activeMutationCount === 0 && qaQuestion && (
          <Badge color='#fff7e6' styles={{ label: { color: '#B85A00' } }}>
            {t('Unsaved changes')}
          </Badge>
        )}
        {activeMutationCount === 0 && !qaQuestion && (
          <Badge color='#d5f5f6' styles={{ label: { color: '#047481' } }}>
            {t('Saved')}
          </Badge>
        )}
      </span>
    </header>
  )
}
